import { useEffect, useState } from 'react';

import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { sessionsSettingsApi } from '@hotjar/webapp/features/Settings/api';
import { useCurrentSite } from '@hotjar/site';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';

import { TrackingIssue } from './TrackingIssue';
import { SessionCaptureDisabled } from './SessionCaptureDisabled';
import { TrackingBasic } from './TrackingBasic';
import { SessionTrafficCoverage } from './SessionTrafficCoverage';

const SamplingWidget = (): JSX.Element | null => {
  const currentSite = useCurrentSite().data;
  const { coveragePercentage } = useTrafficCoverage();
  const { click, viewElement } = useTrack();
  const { getState } = useTrackingCode();
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionCaptureEnabled, setIsSessionCaptureEnabled] = useState(false);
  const [isUsingTrafficCoverage, setIsUsingTrafficCoverage] = useState(false);
  const planRepository = usePlanRepository();
  const hasCC = currentSite
    ? planRepository.byId(currentSite.effectivePlanId).ccDailyAllowance > 0
    : false;
  const trackingCodeState = getState(currentSite?.lastData);

  const trackIndicatorClick = (elementName: string) => {
    if (currentSite) {
      click(elementName, 'header_sampling.widget.indicator', {
        account_id: currentSite.account.id,
        organization_id: currentSite.organization.id,
        site_id: currentSite.id,
        shown_traffic_coverage: coveragePercentage,
      });
    }
  };

  const trackClick = (elementName: string) => {
    if (currentSite) {
      click(elementName, 'header_sampling.widget', {
        account_id: currentSite.account.id,
        organization_id: currentSite.organization.id,
        site_id: currentSite.id,
        shown_traffic_coverage: coveragePercentage,
      });
    }
  };
  const trackView = (elementName: string) => {
    if (currentSite) {
      viewElement(elementName, 'header_sampling.widget', {
        account_id: currentSite.account.id,
        organization_id: currentSite.organization.id,
        site_id: currentSite.id,
        shown_traffic_coverage: coveragePercentage,
      });
    }
  };

  useEffect(() => {
    // no need to make an api request if TC is not installed
    if (!trackingCodeState.installed || !currentSite) {
      return;
    }
    // get switch state from api
    (async () => {
      setIsLoading(true);
      const response = await sessionsSettingsApi.get(currentSite.id);
      if (response.success) {
        const newIsSessionCaptureEnabled = response.data?.session_capture_mode === 'on';
        setIsSessionCaptureEnabled(newIsSessionCaptureEnabled);
        if (trackingCodeState.isActive && newIsSessionCaptureEnabled && hasCC) {
          setIsUsingTrafficCoverage(true);
        }
      }
      setIsLoading(false);
    })();
  }, [currentSite, trackingCodeState.installed, trackingCodeState.isActive, hasCC]);
  if (isLoading || !trackingCodeState.installed) {
    return null;
  }

  if (!trackingCodeState.isActive) {
    return (
      <TrackingIssue
        trackIndicatorClick={trackIndicatorClick}
        trackClick={trackClick}
        trackView={trackView}
      />
    );
  }

  if (!isSessionCaptureEnabled && hasCC) {
    return (
      <SessionCaptureDisabled
        trackIndicatorClick={trackIndicatorClick}
        trackClick={trackClick}
        trackView={trackView}
      />
    );
  }

  if (isSessionCaptureEnabled && hasCC && isUsingTrafficCoverage) {
    return (
      <SessionTrafficCoverage
        trackIndicatorClick={trackIndicatorClick}
        trackClick={trackClick}
        trackView={trackView}
      />
    );
  }

  return <TrackingBasic trackIndicatorClick={trackIndicatorClick} />;
};

export { SamplingWidget };
